import { useMemo } from "react";
import { CalendarClock, Layers, Target, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { asArray } from "@/lib/safe";
import { formatMontoMoneda, formatTotalesCLP } from "@/lib/monto";
import { useIndicadores } from "@/hooks/use-indicadores";
import { evaluarElegibilidad } from "@/lib/licitacion-elegibilidad";
import { cn } from "@/lib/utils";
import type { LicitacionEstado, PipelineStage } from "@/types";

const OPEN_STAGES: LicitacionEstado[] = ["detectada", "analisis", "viable", "preparacion", "postulada", "evaluacion"];

export function formatMoney(amount: number, currency: string) {
  return formatMontoMoneda(amount, currency);
}

export interface PipelineKpiData {
  montosAbiertos: Record<string, number>;
  sinMonto: number;
  scorePromedio: number | null;
  abiertas: number;
  totales: number;
  prioridadAlta: number;
  cierran7Dias: number;
  /** Subconjunto de cierran7Dias que además está "por vencer" (≤48h) — mismo umbral que el badge de la tarjeta. */
  porVencerLicitacion: number;
  porVencerCompraAgil: number;
  vencidas: number;
  adjudicadas: number;
  perdidas: number;
  tasaAdjudicacion: number | null;
}

export function computePipelineKpis(rawPipeline: PipelineStage[], now = new Date()): PipelineKpiData {
  const pipeline = asArray<PipelineStage>(rawPipeline);
  const open = pipeline.filter((stage) => OPEN_STAGES.includes(stage.estado));
  const openItems = open.flatMap((stage) => asArray<PipelineStage["items"][number]>(stage.items));


  const montosAbiertos: Record<string, number> = {};
  let sinMonto = 0;
  openItems.forEach((item) => {
    if (item.montoEstimado == null) { sinMonto += 1; return; }
    const moneda = item.moneda || "CLP";
    montosAbiertos[moneda] = (montosAbiertos[moneda] ?? 0) + item.montoEstimado;
  });

  const scored = openItems.filter((item) => typeof item.scoreIA === "number" && item.scoreIA > 0);
  const scorePromedio = scored.length ? Math.round(scored.reduce((sum, item) => sum + item.scoreIA, 0) / scored.length) : null;

  const limit = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  let cierran7Dias = 0;
  let vencidas = 0;
  openItems.forEach((item) => {
    if (!item.fechaCierre) return;
    const cierre = new Date(item.fechaCierre);
    if (Number.isNaN(cierre.getTime())) return;
    if (cierre.getTime() < now.getTime()) { vencidas += 1; return; }
    if (cierre.getTime() <= limit.getTime()) cierran7Dias += 1;
  });

  // "Cierran en 7 días" mezcla escalas de urgencia muy distintas (licitación:
  // semanas de plazo típico; Compra Ágil: horas/días) — el desglose por
  // "por vencer" (≤48h, mismo umbral que el badge de la tarjeta) separa cuál
  // de los dos tipos realmente necesita atención inmediata.
  const porVencerLicitacion = openItems.filter(
    (item) => item.tipo === "licitacion" && evaluarElegibilidad(item).estado === "por-vencer",
  ).length;
  const porVencerCompraAgil = openItems.filter(
    (item) => item.tipo === "compra-agil" && evaluarElegibilidad(item).estado === "por-vencer",
  ).length;

  const stageTotal = (estado: LicitacionEstado) => pipeline.find((stage) => stage.estado === estado)?.total ?? 0;
  const adjudicadas = stageTotal("adjudicada");
  const perdidas = stageTotal("perdida");
  const cerradas = adjudicadas + perdidas;

  return {
    montosAbiertos,
    sinMonto,
    scorePromedio,
    abiertas: openItems.length,
    totales: pipeline.reduce((sum, stage) => sum + stage.total, 0),
    prioridadAlta: openItems.filter((item) => item.prioridad === "alta").length,
    cierran7Dias,
    porVencerLicitacion,
    porVencerCompraAgil,
    vencidas,
    adjudicadas,
    perdidas,
    tasaAdjudicacion: cerradas ? Math.round((adjudicadas / cerradas) * 100) : null,
  };
}

function Card({ icon, label, value, hint, tone }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: React.ReactNode; tone?: "default" | "warning" | "danger" }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <span className={cn("shrink-0", tone === "danger" && "text-destructive", tone === "warning" && "text-warning")}>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={cn("truncate text-lg font-semibold tabular-nums", tone === "danger" && "text-destructive", tone === "warning" && "text-warning")}>{value}</div>
      {hint && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function PipelineKpisSkeleton() {
  return (
    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[86px] rounded-md" />)}
    </div>
  );
}

export function PipelineKpis({ pipeline }: { pipeline: PipelineStage[] }) {
  const kpis = useMemo(() => computePipelineKpis(pipeline), [pipeline]);
  const { indicadores } = useIndicadores();

  const totales = formatTotalesCLP(kpis.montosAbiertos, indicadores);
  const montoLabel = totales.label;
  const porVencerTotal = kpis.porVencerLicitacion + kpis.porVencerCompraAgil;
  const proximosTone = kpis.vencidas > 0 || porVencerTotal > 0 ? "danger" : kpis.cierran7Dias > 0 ? "warning" : "default";
  const proximosHint = [
    kpis.vencidas ? `${kpis.vencidas} ya vencidas` : null,
    porVencerTotal
      ? `${porVencerTotal} por vencer en ≤48h (${kpis.porVencerLicitacion} licitación · ${kpis.porVencerCompraAgil} Compra Ágil)`
      : "Sin oportunidades por vencer en ≤48h",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Card
        icon={<Layers className="size-4" />}
        label="Pipeline abierto"
        value={montoLabel}
        hint={[kpis.sinMonto ? `${kpis.sinMonto} sin monto informado` : null, kpis.scorePromedio != null ? `Score IA promedio ${kpis.scorePromedio}` : "Score IA pendiente"].filter(Boolean).join(" · ")}
      />
      <Card
        icon={<Target className="size-4" />}
        label="Oportunidades activas"
        value={kpis.abiertas}
        hint={`De ${kpis.totales} totales · ${kpis.prioridadAlta} de prioridad alta`}
      />
      <Card
        icon={<CalendarClock className="size-4" />}
        label="Cierran en 7 días"
        value={kpis.cierran7Dias}
        tone={proximosTone}
        hint={proximosHint}
      />
      <Card
        icon={<TrendingUp className="size-4" />}
        label="Tasa de adjudicación"
        value={kpis.tasaAdjudicacion == null ? "Sin resultados aún" : `${kpis.tasaAdjudicacion}%`}
        hint={`${kpis.adjudicadas} adjudicadas · ${kpis.perdidas} perdidas`}
      />
    </div>
  );
}
